import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import APP_CONFIG, { DASHBOARD_URL } from "../../../constants/app";

/* ─────────────────────────────────────────────────────────
   Fintech Light Mode Theme — Sign In OTP Verification Modal
───────────────────────────────────────────────────────── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  /* ── CSS Reset & Font Scoping ── */
  .mk-otp-root *, .mk-otp-root *::before, .mk-otp-root *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .mk-otp-root {
    min-height: 100vh;
    display: flex;
    font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #f8fafc;
    color: #0f172a;
    position: relative;
    overflow-x: hidden;
  }

  /* ── Background Ambient Glows & Grid ── */
  .mk-bg-decor {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;
  }

  .mk-bg-grid {
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(to right, rgba(226, 232, 240, 0.6) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(226, 232, 240, 0.6) 1px, transparent 1px);
    background-size: 40px 40px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 60%, transparent 100%);
    -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, #000 60%, transparent 100%);
  }

  .mk-bg-glow-1 {
    position: absolute;
    width: 650px;
    height: 650px;
    top: -150px;
    left: -100px;
    background: radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, rgba(59, 130, 246, 0.02) 50%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  .mk-bg-glow-2 {
    position: absolute;
    width: 550px;
    height: 550px;
    bottom: -100px;
    right: 25%;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.07) 0%, rgba(5, 150, 105, 0.02) 50%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  .mk-bg-glow-3 {
    position: absolute;
    width: 450px;
    height: 450px;
    top: 30%;
    right: -100px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%);
    filter: blur(40px);
    border-radius: 50%;
  }

  /* ── Layout Panels ── */
  .mk-main-container {
    display: flex;
    width: 100%;
    min-height: 100vh;
    position: relative;
    z-index: 1;
  }

  /* ── Left Hero Showcase Panel ── */
  .mk-hero-side {
    flex: 1.15;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 48px 56px;
    position: relative;
    z-index: 2;
  }

  .mk-brand-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 32px;
  }

  .mk-logo-img {
    height: 40px;
    max-width: 180px;
    object-fit: contain;
  }

  .mk-brand-fallback {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mk-brand-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #2563eb, #059669);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-weight: 800;
    font-size: 18px;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
  }

  .mk-brand-name {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0f172a;
  }

  .mk-hero-content {
    max-width: 580px;
    margin: auto 0;
    padding: 20px 0;
  }

  .mk-tag-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    padding: 6px 14px;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 600;
    color: #2563eb;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  }

  .mk-tag-pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
    animation: mk-pulse-anim 2s infinite;
  }

  @keyframes mk-pulse-anim {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(0.9); }
  }

  .mk-hero-title {
    font-size: clamp(2rem, 3.2vw, 2.75rem);
    font-weight: 800;
    line-height: 1.18;
    letter-spacing: -0.03em;
    color: #0f172a;
    margin-bottom: 16px;
  }

  .mk-hero-title .mk-highlight-blue {
    color: #2563eb;
    background: linear-gradient(135deg, #1d4ed8, #2563eb);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .mk-hero-title .mk-highlight-green {
    color: #059669;
    background: linear-gradient(135deg, #059669, #10b981);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .mk-hero-desc {
    font-size: 15px;
    line-height: 1.65;
    color: #64748b;
    margin-bottom: 32px;
    font-weight: 450;
  }

  /* ── 3 Feature Pillars: Payout, Wallet, Ledger ── */
  .mk-features-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 36px;
  }

  .mk-feature-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 18px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.03);
    backdrop-filter: blur(10px);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }

  .mk-feature-card:hover {
    transform: translateY(-2px);
    border-color: #cbd5e1;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  }

  .mk-feature-icon-box {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .mk-icon-payout {
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #dbeafe;
  }

  .mk-icon-wallet {
    background: #ecfdf5;
    color: #059669;
    border: 1px solid #d1fae5;
  }

  .mk-icon-ledger {
    background: #faf5ff;
    color: #7c3aed;
    border: 1px solid #f3e8ff;
  }

  .mk-feature-info {
    flex: 1;
    min-width: 0;
  }

  .mk-feature-heading-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2px;
  }

  .mk-feature-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .mk-feature-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .mk-badge-blue { background: #dbeafe; color: #1e40af; }
  .mk-badge-green { background: #d1fae5; color: #065f46; }
  .mk-badge-purple { background: #ede9fe; color: #5b21b6; }

  .mk-feature-subtitle {
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
  }

  /* ── Hero Footer Metrics ── */
  .mk-hero-footer {
    display: flex;
    align-items: center;
    gap: 28px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
  }

  .mk-stat-item {
    display: flex;
    flex-direction: column;
  }

  .mk-stat-number {
    font-family: 'JetBrains Mono', monospace;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }

  .mk-stat-label {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* ── Right Form Container Panel ── */
  .mk-form-side {
    width: 480px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 36px;
    position: relative;
    z-index: 2;
  }

  .mk-card-wrapper {
    width: 100%;
    max-width: 420px;
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 20px;
    padding: 36px 32px;
    box-shadow: 
      0 20px 40px -15px rgba(15, 23, 42, 0.07),
      0 1px 3px rgba(15, 23, 42, 0.04);
    position: relative;
  }

  .mk-form-header {
    margin-bottom: 24px;
  }

  .mk-card-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 4px 10px;
    border-radius: 6px;
    margin-bottom: 12px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .mk-form-heading {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    margin-bottom: 6px;
  }

  .mk-form-subtext {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  /* ── Masked Phone Pill ── */
  .mk-phone-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 4px 12px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
  }

  /* ── Alerts ── */
  .mk-alert-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    animation: mk-shake 0.3s ease-in-out;
  }

  .mk-alert-success {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #059669;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @keyframes mk-shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }

  /* ── OTP Digit Input Row ── */
  .mk-otp-box-row {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin: 20px 0 16px 0;
  }

  .mk-digit-box {
    width: 56px;
    height: 60px;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    text-align: center;
    outline: none;
    transition: all 0.2s ease;
    -moz-appearance: textfield;
  }

  .mk-digit-box::-webkit-outer-spin-button,
  .mk-digit-box::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .mk-digit-box:focus {
    background: #ffffff;
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
    transform: translateY(-2px);
  }

  .mk-digit-box.filled {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #1d4ed8;
  }

  .mk-digit-box.error {
    border-color: #ef4444 !important;
    background: #fffbfa !important;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
    color: #dc2626 !important;
  }

  /* ── Progress Indicator Dots ── */
  .mk-progress-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 22px;
  }

  .mk-pdot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #e2e8f0;
    transition: all 0.2s ease;
  }

  .mk-pdot.filled {
    background: #2563eb;
    transform: scale(1.2);
  }

  .mk-pdot.active {
    background: #059669;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
  }

  /* ── Resend Row ── */
  .mk-resend-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    font-size: 12px;
  }

  .mk-resend-label {
    color: #64748b;
    font-weight: 500;
  }

  .mk-timer-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 2px 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
  }

  .mk-resend-action-btn {
    background: none;
    border: none;
    color: #2563eb;
    font-weight: 700;
    font-size: 12px;
    font-family: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    transition: color 0.15s ease;
  }

  .mk-resend-action-btn:hover:not(:disabled) {
    color: #1d4ed8;
    text-decoration: underline;
  }

  .mk-resend-action-btn:disabled {
    color: #94a3b8;
    cursor: not-allowed;
    text-decoration: none;
  }

  .mk-max-attempts {
    color: #dc2626;
    font-weight: 600;
    font-size: 11px;
  }

  /* ── Submit Action Button ── */
  .mk-submit-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #0f172a, #1e293b);
    border: none;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.18);
    transition: all 0.2s ease;
    margin-bottom: 12px;
  }

  .mk-submit-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #1e293b, #334155);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.24);
  }

  .mk-submit-btn:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
  }

  .mk-submit-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  /* ── Secondary Back Button ── */
  .mk-back-btn {
    width: 100%;
    height: 44px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.15s ease;
  }

  .mk-back-btn:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #0f172a;
  }

  .mk-back-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ── Trust & Security Badges Footer ── */
  .mk-security-badges {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px dashed #e2e8f0;
  }

  .mk-security-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    font-weight: 600;
    color: #64748b;
    letter-spacing: 0.02em;
  }

  .mk-security-icon {
    color: #059669;
    font-size: 12px;
  }

  /* ── Responsive Behavior ── */
  @media (max-width: 1024px) {
    .mk-hero-side {
      display: none;
    }
    .mk-form-side {
      width: 100%;
      max-width: 500px;
      margin: 0 auto;
      padding: 32px 20px;
    }
    .mk-card-wrapper {
      max-width: 100%;
      padding: 32px 24px;
    }
  }

  @media (max-width: 480px) {
    .mk-form-side {
      padding: 20px 14px;
    }
    .mk-card-wrapper {
      padding: 24px 18px;
      border-radius: 16px;
    }
    .mk-digit-box {
      width: 46px;
      height: 52px;
      font-size: 18px;
    }
    .mk-form-heading {
      font-size: 21px;
    }
  }
`;

/* ── Crisp Vector SVGs ── */
const ShieldCheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const PayoutIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const WalletIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
    <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
    <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
  </svg>
);

const LedgerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <line x1="8" y1="7" x2="16" y2="7" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ─────────────────────────────────────────────────────────
   OTP Digit Input Component with Key Handling
───────────────────────────────────────────────────────── */
const OTPInput = ({ length = 4, value = "", onChange, onComplete, disabled = false, autoFocus = true, error = false }) => {
  const [otpValues, setOtpValues] = useState(
    Array(length).fill("").map((_, i) => value[i] || "")
  );
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) inputRefs.current[0].focus();
  }, [autoFocus]);

  useEffect(() => {
    setOtpValues(Array(length).fill("").map((_, i) => value[i] || ""));
  }, [value, length]);

  const handleChange = (index, inputValue) => {
    if (!/^\d*$/.test(inputValue)) return;
    const newValues = [...otpValues];
    newValues[index] = inputValue;
    setOtpValues(newValues);
    const joined = newValues.join("");
    onChange(joined);
    if (inputValue && index < length - 1) inputRefs.current[index + 1]?.focus();
    if (joined.length === length && onComplete) onComplete(joined);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newValues = [...otpValues];
        newValues[index] = "";
        setOtpValues(newValues);
        onChange(newValues.join(""));
      }
    }
    if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < length - 1) inputRefs.current[index + 1]?.focus();
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, "").slice(0, length);
        const newValues = Array(length).fill("").map((_, i) => digits[i] || "");
        setOtpValues(newValues);
        onChange(newValues.join(""));
        const nextIdx = Math.min(digits.length, length - 1);
        inputRefs.current[nextIdx]?.focus();
        if (digits.length === length && onComplete) onComplete(digits);
      });
    }
  };

  return (
    <div className="mk-otp-box-row">
      {otpValues.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          className={`mk-digit-box${error ? " error" : ""}${digit ? " filled" : ""}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          autoComplete="off"
        />
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Main OTPModal Component
───────────────────────────────────────────────────────── */
const OTPModal = ({
  phoneNumber,
  password,
  userToken,
  otpLength = 4,
  onSuccess,
  onError,
  onResend,
  onBack,
  verifyOTPService,
  resendWaitMinutes = 0.5,
  maxResendAttempts = 3,
  autoSubmitOnComplete = false,
  title = "Verify OTP",
  subtitle = "Enter the 4-digit code sent to your registered mobile phone",
}) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  const navigate = useNavigate();
  const cooldownSeconds = Math.floor(resendWaitMinutes * 60);

  useEffect(() => {
    let t;
    if (resendCooldown > 0) t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const formatTime = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const canResend = resendAttempts < maxResendAttempts && resendCooldown === 0 && !resendLoading;

  const maskPhone = (phone) => {
    if (!phone) return "";
    const s = phone.toString();
    if (s.length <= 4) return s;
    return s.slice(0, 2) + "••••••" + s.slice(-2);
  };

  const handleOTPChange = (val) => {
    setOtp(val);
    if (error) setError("");
    if (resendSuccess) setResendSuccess(false);
  };

  const handleOTPComplete = (val) => {
    setOtp(val);
    if (autoSubmitOnComplete) handleSubmit(null, val);
  };

  const handleSubmit = async (e, otpValue = otp) => {
    if (e) e.preventDefault();
    if (otpValue.length !== otpLength) {
      setError(`Please enter the complete ${otpLength}-digit verification code`);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("phone", phoneNumber);
      formData.append("otp", otpValue);
      formData.append("password", password);
      formData.append("fcm_token", localStorage.getItem("fcmToken") || "");
      const result = await verifyOTPService(formData);
      if (result?.status) {
        onSuccess && onSuccess(result);
        localStorage.setItem("userData", JSON.stringify(result.data));
        navigate(DASHBOARD_URL[result.data.role] || "/admin_dashboard", { replace: true });
      } else {
        setError(result.data?.error || result.message || "Invalid verification code. Please try again.");
        setOtp("");
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError("An unexpected error occurred. Please try again.");
      onError && onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setResendLoading(true);
    setError("");
    setResendSuccess(false);
    try {
      const result = await onResend();
      if (result?.status) {
        setResendCooldown(cooldownSeconds);
        setResendAttempts((p) => p + 1);
        setOtp("");
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 4000);
      } else {
        setError(result?.data?.error || result?.message || "Failed to resend verification code.");
      }
    } catch {
      setError("Failed to resend verification code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const filled = otp.length;
  const brandName = APP_CONFIG.ORGANISATION_BRAND || "Mokshiq";
  const brandLogo = APP_CONFIG.ORGANISATION_LOGO;

  return (
    <>
      <style>{styles}</style>

      <div className="mk-otp-root">
        {/* Background glow and decorative elements */}
        <div className="mk-bg-decor" aria-hidden="true">
          <div className="mk-bg-grid" />
          <div className="mk-bg-glow-1" />
          <div className="mk-bg-glow-2" />
          <div className="mk-bg-glow-3" />
        </div>

        <div className="mk-main-container">
          {/* ── Left Hero Showcase Panel ── */}
          <div className="mk-hero-side">
            <div className="mk-brand-header">
              {brandLogo ? (
                <img src={brandLogo} alt={brandName} className="mk-logo-img" />
              ) : (
                <div className="mk-brand-fallback">
                  <div className="mk-brand-icon">M</div>
                  <span className="mk-brand-name">{brandName}</span>
                </div>
              )}
            </div>

            <div className="mk-hero-content">
              <div className="mk-tag-pill">
                <span className="mk-tag-pulse" />
                <span>One Secure Dashboard • Security Check</span>
              </div>

              <h1 className="mk-hero-title">
                <span className="mk-highlight-blue">Payout</span>,{" "}
                <span className="mk-highlight-green">Wallet</span> &{" "}
                Ledger in One Secure Dashboard.
              </h1>

              <p className="mk-hero-desc">
                Two-step verification protects your funds from unauthorized access.
                Complete mobile verification to unlock your merchant console.
              </p>

              {/* 3 Core Value Proposition Feature Cards */}
              <div className="mk-features-grid">
                {/* 1. Instant Payout Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-payout">
                    <PayoutIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Instant Payouts</span>
                      <span className="mk-feature-badge mk-badge-blue">24/7 IMPS/NEFT</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Sub-second automated bank transfers & bulk merchant payouts with instant UTR tracking.
                    </p>
                  </div>
                </div>

                {/* 2. Multi-Wallet Control Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-wallet">
                    <WalletIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Multi-Wallet Control</span>
                      <span className="mk-feature-badge mk-badge-green">Main & BC Wallet</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Intra-wallet balance transfers, QR top-ups, and automated low-balance safeguards.
                    </p>
                  </div>
                </div>

                {/* 3. Audit-Ready Ledger Card */}
                <div className="mk-feature-card">
                  <div className="mk-feature-icon-box mk-icon-ledger">
                    <LedgerIcon />
                  </div>
                  <div className="mk-feature-info">
                    <div className="mk-feature-heading-row">
                      <span className="mk-feature-title">Audit-Ready Ledger</span>
                      <span className="mk-feature-badge mk-badge-purple">100% Reconciled</span>
                    </div>
                    <p className="mk-feature-subtitle">
                      Immutable double-entry transaction log with automated GST & CA reporting exports.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Footer Stats */}
            <div className="mk-hero-footer">
              <div className="mk-stat-item">
                <span className="mk-stat-number">99.98%</span>
                <span className="mk-stat-label">Platform Uptime</span>
              </div>
              <div className="mk-stat-item">
                <span className="mk-stat-number">₹450M+</span>
                <span className="mk-stat-label">Monthly Volume</span>
              </div>
              <div className="mk-stat-item">
                <span className="mk-stat-number">256-Bit</span>
                <span className="mk-stat-label">Bank Encryption</span>
              </div>
            </div>
          </div>

          {/* ── Right Form Container Panel ── */}
          <div className="mk-form-side">
            <div className="mk-card-wrapper">
              <div className="mk-form-header">
                <div className="mk-card-pill">
                  <ShieldCheckIcon />
                  <span>Two-Factor Authentication</span>
                </div>
                <h2 className="mk-form-heading">{title}</h2>
                <p className="mk-form-subtext">{subtitle}</p>
                {phoneNumber && (
                  <div className="mk-phone-tag">
                    📞 {maskPhone(phoneNumber)}
                  </div>
                )}
              </div>

              {/* Alerts */}
              {error && (
                <div className="mk-alert-error" role="alert">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              {resendSuccess && !error && (
                <div className="mk-alert-success" role="alert">
                  <span>✅</span>
                  <span>New verification code sent successfully!</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Digit boxes */}
                <OTPInput
                  length={otpLength}
                  value={otp}
                  onChange={handleOTPChange}
                  onComplete={handleOTPComplete}
                  disabled={loading}
                  error={!!error}
                />

                {/* Progress dots */}
                <div className="mk-progress-dots">
                  {Array.from({ length: otpLength }).map((_, i) => (
                    <div
                      key={i}
                      className={`mk-pdot${i < filled ? " filled" : ""}${i === filled ? " active" : ""}`}
                    />
                  ))}
                </div>

                {/* Resend row */}
                <div className="mk-resend-row">
                  <span className="mk-resend-label">Didn't receive code?</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {resendCooldown > 0 && (
                      <span className="mk-timer-pill">⏱ {formatTime(resendCooldown)}</span>
                    )}
                    {resendAttempts < maxResendAttempts ? (
                      <button
                        type="button"
                        className="mk-resend-action-btn"
                        onClick={handleResend}
                        disabled={!canResend}
                      >
                        {resendLoading ? (
                          "Sending…"
                        ) : (
                          <>
                            Resend Code
                            {resendAttempts > 0 && (
                              <span style={{ fontSize: "10px", color: "#94a3b8" }}>
                                ({resendAttempts}/{maxResendAttempts})
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="mk-max-attempts">Max attempts reached</span>
                    )}
                  </div>
                </div>

                {/* Submit Action Button */}
                <button
                  type="submit"
                  className="mk-submit-btn"
                  disabled={loading || otp.length !== otpLength}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                        style={{ width: "1.1rem", height: "1.1rem", borderWidth: "2px" }}
                      />
                      <span>Verifying &amp; Authorizing…</span>
                    </>
                  ) : (
                    <>
                      <span>Verify &amp; Continue</span>
                      <ArrowRightIcon />
                    </>
                  )}
                </button>

                {/* Back to Login Button */}
                {onBack && (
                  <button
                    type="button"
                    className="mk-back-btn"
                    onClick={onBack}
                    disabled={loading}
                  >
                    ← Back to Login
                  </button>
                )}
              </form>

              {/* Security Trust Badges */}
              <div className="mk-security-badges">
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>256-Bit SSL</span>
                </div>
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>OTP Protected</span>
                </div>
                <div className="mk-security-item">
                  <span className="mk-security-icon">✓</span>
                  <span>PCI DSS Ready</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OTPModal;